// @flow

import React, { Children, useState, useEffect, useCallback } from 'react';
import type { Node } from 'react';
import store from 'store2';

import { captureException } from 'shared/utils/errorTracking/errorTracking';
import { localStorageAvailable } from 'shared/utils/localStorage';

import { FormContext } from './FormContext';
import type { FieldMap } from './FormContext';

type Props<S> = {
  children: Node,
  submit: (state: S | {}) => Promise<void>,
  initialState: S,
  localStorageKey: string,
  doNotPersistLocalStorage: boolean
};

function getInitialState<S>(key: string, initialState: S): S | {} {
  return localStorageAvailable() ? store.get(key, initialState) : initialState;
}

export function FormFieldsProvider<S: FieldMap>({
  children,
  submit,
  initialState,
  localStorageKey,
  doNotPersistLocalStorage
}: Props<S>) {
  const [state, setState] = useState<S | {}>(
    doNotPersistLocalStorage
      ? initialState
      : getInitialState<S>(localStorageKey, initialState)
  );
  // We want to ensure submit happens with the latest state,
  // so our public submit just changes this different state we keep track of
  const [shouldSubmit, setShouldSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const publicSubmit = useCallback(() => {
    setShouldSubmit(true);
  }, []);

  useEffect(() => {
    if (shouldSubmit) {
      setIsSubmitting(true);

      (async () => {
        try {
          await submit(state);
        } finally {
          setIsSubmitting(false);
        }
      })();
      setShouldSubmit(false);
    }
  }, [state, shouldSubmit, submit]);

  const setFields = useCallback(
    (fields) => setState({ ...state, ...fields }),
    [state]
  );

  // every time the state changes, store it in the given localStorageKey
  useEffect(() => {
    if (!doNotPersistLocalStorage) {
      try {
        store.set(localStorageKey, state);
      } catch (err) {
        captureException(
          new Error(
            `FormFieldsProvider store to localStorage "${
              err.name
            }" error: ${err.toString()}`
          )
        );
      }
    }
  }, [state, localStorageKey, doNotPersistLocalStorage]);

  return (
    <FormContext.Provider
      value={{ fields: state, setFields, submit: publicSubmit, isSubmitting }}>
      {Children.only(children)}
    </FormContext.Provider>
  );
}

FormFieldsProvider.defaultProps = {
  initialState: {}
};
